import React from 'react'
import { IMPORT_FAILED_TEXT } from 'src/utils/importRefusal'
import { Box, CircularProgress, LinearProgress, TextField, Typography } from '@material-ui/core'
import {
  CheckCircleOutline as StartedIcon,
  HelpOutline as UnknownIcon,
  WarningAmberRounded as WarningIcon,
  RadioButtonUnchecked as QueuedIcon,
} from '@material-ui/icons'
import FileNameText from 'src/components/FileNameText'
import { START_ERRORS } from 'src/services/analysisStart'
import { GroupLabel, Muted, TextLink } from './components/ui'
import { setAsideFiles } from './components/SetAsideNote'
import { workflowSentence } from './model/workflow'
import { aiCounts, effectiveAI } from './model/samples'
import { UNKNOWN_PRICE_TEXT, creditsText, formatCredits, partsText } from './model/pricing'
import useNameWithDefault from './components/useNameWithDefault'

// The review before a batch starts, and the progress while it starts. Both sit in
// one centred 880px column. Files set aside for another batch (another input or
// analysis) are named as waiting, not as "Not included": they start in a batch of
// their own, which the progress view offers once this one has run.

const Section = ({ label, children, sx }) => (
  <Box sx={{ mt: 4, ...sx }}>
    {label && <GroupLabel>{label}</GroupLabel>}
    {children}
  </Box>
)

// Price lines share one grid, so the "each" and amount columns line up:
//   36 × Whole exome     160 each    5,760
const PRICE_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto minmax(96px, auto)',
  columnGap: 3,
  alignItems: 'center',
  maxWidth: 560,
}

const PriceCell = ({ children, strong, muted, right, small }) => (
  <Typography
    sx={{
      fontSize: small ? 13.5 : 14.5,
      lineHeight: '30px',
      fontWeight: strong ? 600 : 400,
      textAlign: right ? 'right' : 'left',
      fontVariantNumeric: 'tabular-nums',
      color: muted ? 'text.secondary' : 'text.primary',
    }}
  >
    {children}
  </Typography>
)

const PriceRow = ({ label, unit, amount, strong, muted }) => (
  <>
    <PriceCell strong={strong} muted={muted}>
      {label}
    </PriceCell>
    <PriceCell muted right small>
      {unit || ''}
    </PriceCell>
    <PriceCell strong={strong} muted={muted} right>
      {amount}
    </PriceCell>
  </>
)

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`

// Why a batch stops early: starts go one at a time and stop at the first one the
// balance cannot cover. noun: what each start is, [one, many].
export const shortBalanceText = (price, count, noun = ['sample', 'samples']) => {
  if (!price || !price.known || !(price.short > 0)) return ''
  const short = `${creditsText(price.short)} short`
  const samples = plural(count, noun[0], noun[1])
  if (typeof price.covers !== 'number') return `The balance is ${short}.`
  if (price.covers === 0) return `The balance covers none of the ${samples}, ${short}. Top up to start.`
  return `The balance covers the first ${price.covers} of ${samples}, ${short}. Retry the rest after a top-up.`
}

/**
 * The Price section of a review: a batchPrice or groupPrice estimate (model/pricing)
 * as lines, the total, the balance and what is left, or why the balance falls short.
 */
export const PriceSection = ({ price, balance, count, noun }) => {
  if (!price || !price.lines.length) return <Muted>Price confirmed at start.</Muted>
  const known = price.known
  const warning = shortBalanceText(price, count, noun)
  return (
    <>
      <Box sx={PRICE_GRID_SX}>
        {price.lines.map((line) => (
          <PriceRow
            key={line.key || line.label}
            label={`${line.count} × ${line.label}`}
            unit={line.unit !== null ? `${formatCredits(line.unit)} each` : ''}
            amount={line.credits !== null ? formatCredits(line.credits) : UNKNOWN_PRICE_TEXT}
            muted={line.credits === null}
          />
        ))}
        <PriceRow label="Estimated total" amount={known ? formatCredits(price.total) : UNKNOWN_PRICE_TEXT} strong />
        {Number.isFinite(balance) && <PriceRow label="Balance" amount={formatCredits(balance)} muted />}
        {known && Number.isFinite(balance) && !warning && (
          <PriceRow label="Balance after" amount={formatCredits(balance - price.total)} muted />
        )}
      </Box>
      {warning && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1, maxWidth: 560 }}>
          <WarningIcon sx={{ fontSize: 17, color: 'text.primary', mt: '2px' }} />
          <Typography sx={{ fontSize: 14 }}>{warning}</Typography>
        </Box>
      )}
      <Muted sx={{ mt: 1 }}>The server sets the final charge.</Muted>
    </>
  )
}

// One sample in the review list, with its own credits (the parts on hover).
const SampleLine = ({ sample, price }) => (
  <Box
    title={price && price.parts.length ? partsText(price.parts) : undefined}
    sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, minWidth: 0, minHeight: 26 }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <FileNameText name={sample.key} variant="plain" maxLines={1} sx={{ fontSize: 13.5 }} />
    </Box>
    {price && price.credits !== null && (
      <Typography
        sx={{ fontSize: 13, color: 'text.secondary', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
        aria-label={`${sample.key}: ${formatCredits(price.credits)} credits`}
      >
        {formatCredits(price.credits)}
      </Typography>
    )}
  </Box>
)

const SAMPLE_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
  columnGap: 4,
  rowGap: 0,
}

const SampleGrid = ({ samples, perSample }) => (
  <Box sx={SAMPLE_GRID_SX}>
    {samples.map((s) => (
      <SampleLine key={s.uid} sample={s} price={perSample ? perSample.get(s.uid) : null} />
    ))}
  </Box>
)

const SubLabel = ({ children, sx }) => (
  <Typography sx={{ fontSize: 13.5, fontWeight: 500, mb: 0.5, ...sx }}>{children}</Typography>
)

export const reviewHeading = (workflow, count) => {
  const noun = count === 1 ? 'analysis' : 'analyses'
  if (workflow.analysis === 'somatic') {
    if (workflow.input === 'fastq') {
      return `Start ${count} ${workflow.mode === 'tn' ? 'tumour-normal' : 'tumour-only'} ${noun}`
    }
    return `Start ${count} somatic ${noun}`
  }
  return `Start ${count} germline ${noun}`
}

export const ReviewView = ({
  workflow,
  startable,
  excluded,
  warnings,
  price,
  balance,
  name,
  namePlaceholder,
  onNameChange,
  cohortName,
  setAside = [],
}) => {
  // The default name is the field's text: starting never needs a name typed.
  const nameField = useNameWithDefault(name, namePlaceholder, onNameChange)
  // Long-read samples with their own CNV, SV or methylation choice.
  const ownOptions = startable.filter((s) => s.longRead && Object.keys(s.longRead).length > 0).length
  // Each sample's own price (model/pricing batchPrice), and who runs AI (what each
  // start sends). A batch where only some samples run AI says how many, and lists
  // them in two groups.
  const perSample = price?.perSample || null
  const germline = workflow.analysis === 'germline'
  const ai = germline ? aiCounts(workflow, startable) : { withAI: 0, withoutAI: startable.length }
  const withAI = germline ? startable.filter((s) => effectiveAI(workflow, s)) : []
  const mixedAI = ai.withAI > 0 && ai.withoutAI > 0
  // The sentence names the interpretation the samples run with, not the batch default.
  const sentenceWorkflow =
    germline && startable.length
      ? { ...workflow, interpretation: mixedAI ? 'mixed' : ai.withAI ? 'ai' : 'standard' }
      : workflow

  return (
    <Box sx={{ maxWidth: 880, mx: 'auto', py: 2 }}>
      <Typography sx={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em' }}>
        {reviewHeading(workflow, startable.length)}
      </Typography>
      <Typography sx={{ fontSize: 15, color: 'text.secondary', mt: 1 }}>
        {workflowSentence(sentenceWorkflow, { cohortName })}
      </Typography>
      {mixedAI && (
        <Typography sx={{ fontSize: 15, mt: 0.5 }} data-testid="review-ai-counts">
          {`${ai.withAI} with AI · ${ai.withoutAI} without`}
        </Typography>
      )}
      {ownOptions > 0 && (
        <Muted sx={{ mt: 0.5 }}>
          {ownOptions === 1 ? '1 sample uses its own options.' : `${ownOptions} samples use their own options.`}
        </Muted>
      )}

      <Section label="Batch name">
        <TextField
          size="small"
          {...nameField}
          sx={{ width: 400, maxWidth: '100%', '& .MuiInputBase-root': { fontSize: 14.5 } }}
          inputProps={{ 'aria-label': 'Batch name', maxLength: 120 }}
        />
      </Section>

      <Section label="Price">
        <PriceSection price={price} balance={balance} count={startable.length} />
      </Section>

      {warnings.length > 0 && (
        <Section label="Warnings">
          {warnings.map((w) => (
            <Box key={w} sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 26 }}>
              <WarningIcon sx={{ fontSize: 17, color: 'text.primary' }} />
              <Typography sx={{ fontSize: 14 }}>{w}</Typography>
            </Box>
          ))}
        </Section>
      )}

      {excluded.length > 0 && (
        <Section label="Not included">
          <Typography sx={{ fontSize: 14 }}>
            {excluded.length} not included:{' '}
            {Object.entries(
              excluded.reduce((acc, e) => {
                acc[e.reason] = (acc[e.reason] || 0) + 1
                return acc
              }, {}),
            )
              .map(([reason, n]) => `${reason} (${n})`)
              .join(', ')}
          </Typography>
          <Muted sx={{ mt: 0.5, fontFamily: 'fontFamilyMono', fontSize: 12.5 }}>
            {excluded
              .slice(0, 30)
              .map((e) => e.sample.key)
              .join(', ')}
            {excluded.length > 30 ? ` and ${excluded.length - 30} more` : ''}
          </Muted>
        </Section>
      )}

      <Section label={`Samples (${startable.length})`}>
        {mixedAI ? (
          <>
            <SubLabel>{`With AI (${withAI.length})`}</SubLabel>
            <SampleGrid samples={withAI} perSample={perSample} />
            <SubLabel sx={{ mt: 2 }}>{`Without AI (${startable.length - withAI.length})`}</SubLabel>
            <SampleGrid samples={startable.filter((s) => !effectiveAI(workflow, s))} perSample={perSample} />
          </>
        ) : (
          <SampleGrid samples={startable} perSample={perSample} />
        )}
        {setAside.map((group) => (
          <Muted key={group.key} sx={{ mt: 1.5 }}>
            {setAsideFiles(group)} {group.samples.length === 1 ? 'waits for its' : 'wait for their'} own batch.
          </Muted>
        ))}
      </Section>
    </Box>
  )
}

// ----------------------------------------------------------------------

const STATUS_TEXT = {
  queued: 'Queued to start',
  starting: 'Starting',
  started: 'Started',
}

const StatusCell = ({ state }) => {
  const status = state?.status || 'queued'
  if (status === 'started') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <StartedIcon sx={{ fontSize: 17, color: 'success.main' }} />
        <Typography sx={{ fontSize: 14 }}>
          Started
          {state.detail?.warning ? (
            <Typography component="span" sx={{ fontSize: 13, color: 'text.secondary', ml: 1 }}>
              {state.detail.warning}
            </Typography>
          ) : null}
        </Typography>
      </Box>
    )
  }
  if (status === 'starting') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <CircularProgress size={14} thickness={5} />
        <Typography sx={{ fontSize: 14 }}>Starting</Typography>
      </Box>
    )
  }
  // No reply: it may have started, so it reads as a calm note and is not retried.
  if (status === 'unknown') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0, color: 'text.secondary' }}>
        <UnknownIcon sx={{ fontSize: 17, mt: '2px' }} />
        <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
          {state.detail?.error || START_ERRORS.no_reply}
        </Typography>
      </Box>
    )
  }
  // A clean refusal (for example RNA analysis switched off here): nothing was
  // written or charged, so it reads as a plain note with the server's reason.
  if (status === 'failed' && state.detail?.code === 'refused' && !state.detail?.importRefused) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0, color: 'text.secondary' }}>
        <UnknownIcon sx={{ fontSize: 17, mt: '2px' }} />
        <Typography sx={{ fontSize: 14, color: 'text.secondary' }} data-testid="start-refused">
          Not started: {state.detail.error}
        </Typography>
      </Box>
    )
  }
  // A file the backend could not import: the one neutral sentence and nothing more.
  if (status === 'failed' && state.detail?.importRefused) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0, color: 'text.secondary' }}>
        <UnknownIcon sx={{ fontSize: 17, mt: '2px' }} />
        <Typography sx={{ fontSize: 14, color: 'text.secondary' }} data-testid="start-import-refused">
          {IMPORT_FAILED_TEXT}
        </Typography>
      </Box>
    )
  }
  if (status === 'failed' || status === 'skipped') {
    const detail = state.detail || {}
    let reason = detail.error || 'Not started'
    if (detail.code === 'insufficient_balance' && detail.neededBalance) {
      reason = `Not enough balance, needs ${formatCredits(Number(detail.neededBalance))} more`
    }
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        <WarningIcon sx={{ fontSize: 17, color: 'error.main' }} />
        <Typography sx={{ fontSize: 14 }} noWrap title={reason}>
          Not started: {reason.charAt(0).toLowerCase() + reason.slice(1)}
        </Typography>
      </Box>
    )
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
      <QueuedIcon sx={{ fontSize: 16 }} />
      <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{STATUS_TEXT[status] || status}</Typography>
    </Box>
  )
}

// waiting: [{ label, onStart }], the set-aside batches offered once this run is done.
export const StartView = ({ run, waiting = [] }) => {
  const order = run.order
  const states = order.map((uid) => run.status[uid])
  const started = states.filter((s) => s?.status === 'started').length
  const notStarted = states.filter((s) => s?.status === 'failed' || s?.status === 'skipped').length
  const noReply = states.filter((s) => s?.status === 'unknown').length
  const settled = started + notStarted + noReply
  const total = order.length

  let heading
  if (run.running) heading = `Starting ${Math.min(settled + 1, total)} of ${total}`
  else if (notStarted === 0 && noReply === 0) heading = `${started} ${started === 1 ? 'analysis' : 'analyses'} started`
  else {
    heading = [`${started} started`, notStarted && `${notStarted} not started`, noReply && `${noReply} no reply`]
      .filter(Boolean)
      .join('. ')
  }

  return (
    <Box sx={{ maxWidth: 880, mx: 'auto', py: 2 }}>
      <Typography sx={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em' }}>{heading}</Typography>
      <Typography sx={{ fontSize: 15, color: 'text.secondary', mt: 1 }}>
        {run.running
          ? 'Keep this tab open until all samples have started. Samples start one at a time.'
          : run.batch?.name
          ? `Batch ${run.batch.name}.`
          : ''}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={total ? (settled / total) * 100 : 0}
        sx={{ mt: 3, height: 6, borderRadius: 3, maxWidth: 480 }}
      />

      <Box sx={{ mt: 4 }}>
        {order.map((uid) => (
          <Box
            key={uid}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 280px) minmax(0, 1fr)',
              columnGap: 3,
              alignItems: 'center',
              minHeight: 36,
            }}
          >
            <FileNameText name={run.keys[uid]} variant="plain" maxLines={1} sx={{ fontSize: 13.5 }} />
            <StatusCell state={run.status[uid]} />
          </Box>
        ))}
      </Box>

      {!run.running && waiting.length > 0 && (
        <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.75 }}>
          {waiting.map((w) => (
            <TextLink key={w.label} onClick={w.onStart} sx={{ fontSize: 14 }}>
              {w.label}
            </TextLink>
          ))}
        </Box>
      )}
    </Box>
  )
}
